import { useRef, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import cn from 'classnames';
// Api
import {
  GetInterview,
  GetInterviewVariables,
} from 'api/interview/types/GetInterview';
import {
  GET_INTERVIEW,
  GET_INTERVIEW_IMAGE_STATE,
} from 'api/interview/queries';
import {
  GetInterviewImageState,
  GetInterviewImageStateVariables,
} from 'api/interview/types/GetInterviewImageState';
import { InterviewImageStatus } from 'api/graphql-global-types';
// Helpers
import { downloadImageFromUrl } from 'helpers/download';
// Ui
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
import Loader from 'ui3/Loader/Loader';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Image from 'ui3/Image/Image';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './InterviewScheduledStep.module.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

type InterviewScheduledStepProps = {
  interviewId: string;
};

const InterviewScheduledStep = ({
  interviewId,
}: InterviewScheduledStepProps) => {
  const isImageCompletedRef = useRef<boolean>(false);

  const {
    data: interview,
    loading: interviewLoading,
    refetch: refetchInterview,
  } = useQuery<GetInterview, GetInterviewVariables>(GET_INTERVIEW, {
    variables: {
      input: {
        id: interviewId,
      },
    },
    skip: !interviewId,
  });

  const { data: interviewImageState, loading: imageStateLoading } = useQuery<
    GetInterviewImageState,
    GetInterviewImageStateVariables
  >(GET_INTERVIEW_IMAGE_STATE, {
    variables: {
      input: {
        id: interviewId,
      },
    },
    skip: !interviewId || isImageCompletedRef.current,
    pollInterval: isImageCompletedRef.current ? 0 : 2000,
  });

  useEffect(() => {
    if (
      interviewImageState?.getInterviewImageState?.imageStatus ===
      InterviewImageStatus.completed
    ) {
      isImageCompletedRef.current = true;
      refetchInterview();
    }

    // If by any chance the interview image generation fails initially, show toast
    if (
      interviewImageState?.getInterviewImageState?.imageStatus ===
      InterviewImageStatus.failed
    ) {
      isImageCompletedRef.current = true;
      showToast({
        message:
          'Poster generation failed. An automatic retry will follow soon.',
        type: 'error',
        autoClose: 5000,
      });
    }
  }, [
    interviewImageState?.getInterviewImageState?.imageStatus,
    refetchInterview,
  ]);

  const loading =
    interviewLoading || imageStateLoading || !isImageCompletedRef.current;

  const scheduleDateUtc = interview?.getInterview?.stream?.scheduleDateUtc;
  const scheduleDateTz =
    interview?.getInterview?.scheduleEvent?.guestTimezone ||
    interview?.getInterview?.stream?.scheduleDateTz;
  const interviewerName = `${
    interview?.getInterview?.interviewer?.firstName || ''
  } ${interview?.getInterview?.interviewer?.lastName || ''}`;

  const formattedDate = scheduleDateUtc
    ? (() => {
        const userTimezone = scheduleDateTz || dayjs.tz.guess();
        const convertedDate = dayjs(scheduleDateUtc).tz(userTimezone);

        const timezoneAbbr =
          new Intl.DateTimeFormat('en', {
            timeZone: userTimezone,
            timeZoneName: 'short',
          })
            .formatToParts(convertedDate.toDate())
            .find((part) => part.type === 'timeZoneName')?.value ||
          userTimezone;
        return convertedDate.format(
          `MMMM D, YYYY [at] hh:mmA [${timezoneAbbr}]`
        );
      })()
    : '';

  const handleDownloadPoster = () => {
    downloadImageFromUrl(
      interview?.getInterview?.stream?.imageURL ?? '',
      `Interview with ${interviewerName} Poster`
    );
  };

  const handleSharePoster = async () => {
    try {
      await navigator.clipboard.writeText(
        interview?.getInterview?.stream?.imageURL || ''
      );

      showToast({
        message: 'Copied',
        autoClose: 1000,
      });
    } catch (error) {
      showToast({
        message: (error as Error).message || 'Could not copy text',
        type: 'error',
        autoClose: 1000,
      });
    }
  };

  return (
    <div className={styles.root}>
      <Text variant="h4" className={styles.title}>
        Interview Scheduled!
      </Text>
      <div className={styles.description}>
        <Text color="lights-medium">
          Your Interview has been scheduled with {interviewerName} on
        </Text>
        <Text variant="body1Medium16">{formattedDate}</Text>
      </div>

      <div className={styles.bodyContainer}>
        <div
          className={cn(styles.imageContainer, {
            [styles.loading]: loading,
          })}
        >
          <Image
            alt={'Interview Poster'}
            className={styles.posterImage}
            src={
              !loading ? interview?.getInterview?.stream?.imageURL ?? '' : ''
            }
          />
          {loading ? (
            <Loader loading={true} size="xxLarge" />
          ) : (
            <Button
              className={styles.downloadImageButton}
              variant="secondary"
              size="medium"
              onClick={handleDownloadPoster}
            >
              <MillionsIcon name="download" size={20} />
            </Button>
          )}
        </div>
        <div className={styles.buttonsWrapper}>
          <Button
            className={styles.sharePosterButton}
            variant="secondary"
            size="medium"
            disabled={loading}
            onClick={handleSharePoster}
          >
            <MillionsIcon name="share" />
            Share Poster
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InterviewScheduledStep;
